import { useMemo } from "react";
import { Field, Fields } from "redux-form";
import _ from "underscore";

import { Box } from "@braintrust/braintrust-ui-components";
import { CodeIcon } from "@braintrust/braintrust-ui-components/Icons";
import { createFiltersFormAndHook } from "@js/apps/filters";
import { BidsTalentLocationFilter } from "@js/apps/jobs/apps/bids/forms/bid-location-filter";
import { ReactionsFeedbackFilters } from "@js/apps/jobs/components/filtering-actions/reactions-feedback-filters";
import { JOB_EVENTS_PAGE_QUERY_PARAM } from "@js/apps/jobs/components/job-events-button/const";
import { SelectField } from "@js/forms/fields/select/select";
import { useAppSelector } from "@js/hooks/redux";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import type { FetchBidsParamsOrdering } from "../../apps/bids/api/types";
import { getSelectedBidIds } from "../../apps/bids/components/bulk-select-actions/bid-bulk-selection-slice";
import { BidSelectionCheckbox } from "../../apps/bids/components/bulk-select-actions/bid-selection-checkbox";
import { BidSkillsFilter } from "../../apps/bids/forms/bid-skills-filter";

import type { FreelancerBidFilterOptionType } from "./get-options";
import { getOptions } from "./get-options";

import styles from "./style.module.scss";

export type FilteringActionsProps = {
  isMatcher: boolean;
  isAiInterviewEnabled?: boolean;
  job: Pick<
    Job,
    "id" | "job_type" | "job_skills" | "resume_smart_matching_enabled"
  >;
  isInterviewingTab?: boolean;
  bidList: EmployerBidListBid[];
};

type ReactionType = EnumType<typeof ENUMS.BidReactionType>;

const FORMS_BID_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All stages" },
  {
    value: ENUMS.BidStatus.NEW,
    label: ENUMS.BidStatusLabels[ENUMS.BidStatus.NEW],
  },
  {
    value: ENUMS.BidStatus.VIEWED,
    label: ENUMS.BidStatusLabels[ENUMS.BidStatus.VIEWED],
  },
  {
    value: ENUMS.JobInterviewStatus.ROUND_1,
    label: "Round 1",
  },
  {
    value: ENUMS.JobInterviewStatus.ROUND_2,
    label: "Round 2",
  },
  {
    value: ENUMS.BidStatus.INTERVIEWING,
    label: ENUMS.BidStatusLabels[ENUMS.BidStatus.INTERVIEWING],
  },
  {
    value: ENUMS.BidStatus.OFFER_MADE,
    label: ENUMS.BidStatusLabels[ENUMS.BidStatus.OFFER_MADE],
  },
] as const;

export const SORTING_OPTIONS: Record<
  string,
  { label: string; value: FetchBidsParamsOrdering }
> = {
  DATE: { label: "Sorted by date", value: "-created" },
  AI_SCORE: { label: "Sorted by AI interview score", value: "-ai_score" },
  RESUME_SCORE: {
    label: "Sorted by resume match",
    value: "-resume_smart_matching__match_score",
  },
} as const;

type Filters = {
  ordering?: FetchBidsParamsOrdering;
  reaction?: ReactionType;
  is_hidden?: boolean;
  /** when employer use "share" applicants option his team gets email with link containing specific bid ids.  */
  ids?: Array<number>;
  skills?: number[];
  top_skills?: number[];
  search?: string;
};

type BidsFormValues = Filters & {
  status?: (typeof FORMS_BID_STATUS_FILTER_OPTIONS)[number]["value"];
  talent_status: EnumType<typeof ENUMS.FreelancerBidFilterStatus>;
};

export type BidsParamsValues = Filters & {
  status?: Array<EnumType<typeof ENUMS.BidStatus> | "">;
  interview_status?: Array<EnumType<typeof ENUMS.JobInterviewStatus> | "">;
  ids?: Array<number>;
  talent_status: EnumType<typeof ENUMS.FreelancerBidFilterStatus>;
};

const MAPPED_OFFER_MADE_OPTION = [
  ENUMS.BidStatus.OFFER_MADE,
  ENUMS.BidStatus.OFFER_DECLINED,
  ENUMS.BidStatus.OFFER_CANCELED,
  ENUMS.BidStatus.HIRED,
];

const defaultFormValues: BidsFormValues = {
  talent_status: ENUMS.FreelancerBidFilterStatus.ALL,
  status: "",
  ordering: SORTING_OPTIONS.RESUME_SCORE.value,
  reaction: undefined,
  is_hidden: false,
  ids: undefined,
  skills: undefined,
  top_skills: undefined,
};

const defaultParamsValues: BidsParamsValues = {
  talent_status: ENUMS.FreelancerBidFilterStatus.ALL,
  status: [],
  ordering: SORTING_OPTIONS.RESUME_SCORE.value,
  reaction: undefined,
  is_hidden: undefined,
  ids: undefined,
  skills: undefined,
  top_skills: undefined,
};

const normalizeListParam = (
  values: Record<string, unknown>,
  key: keyof Pick<BidsParamsValues, "skills" | "top_skills" | "ids">,
) => {
  const paramValue = values[key];
  if (typeof paramValue === "string") {
    values[key] = paramValue
      .split(",")
      .map((item) => parseInt(item.trim(), 10))
      .filter((numberOrNaN): numberOrNaN is number => !isNaN(numberOrNaN));
  }

  if (typeof paramValue === "number") {
    values[key] = [paramValue];
  }
};

export const [BidListFiltersForm, useBidListFilters] = createFiltersFormAndHook<
  BidsFormValues,
  BidsParamsValues
>(
  {
    submitOnChange: true,
    useAllURLParams: true,
    paramsToIgnore: ["tab", "back", JOB_EVENTS_PAGE_QUERY_PARAM],
    defaultFormValues,
    defaultParamsValues,
    mapValuesToParams: (values) => {
      if (values.status === ENUMS.JobInterviewStatus.ROUND_1) {
        const newValues = {
          ...values,
          interview_status: values.status ? [values.status] : [],
          status: [
            ENUMS.BidStatus.INTERVIEWING,
            ENUMS.BidStatus.IN_REVIEW_AFTER_AI_INTERVIEW,
          ],
        };

        return newValues;
      }

      if (values.status === ENUMS.JobInterviewStatus.ROUND_2) {
        const newValues = {
          ...values,
          interview_status: values.status ? [values.status] : [],
          status: [ENUMS.BidStatus.LIVE_INTERVIEW_AFTER_AI_INTERVIEW],
        };

        return newValues;
      }

      if (values.status === ENUMS.BidStatus.OFFER_MADE) {
        const newValues = {
          ...values,
          status: MAPPED_OFFER_MADE_OPTION,
        };

        return newValues;
      }

      const newValues = {
        ...values,
        status: values.status ? [values.status] : [],
      };

      return newValues;
    },
    mapParamsToValues: (values) => {
      normalizeListParam(values, "ids");
      normalizeListParam(values, "skills");
      normalizeListParam(values, "top_skills");

      if (_.isEqual(values.status, MAPPED_OFFER_MADE_OPTION)) {
        const newValues = {
          ...values,
          status: ENUMS.BidStatus.OFFER_MADE,
        };

        delete newValues.interview_status;

        return newValues;
      }

      const status = values.interview_status
        ? [values.interview_status]
        : values.status;

      const newValues = {
        ...values,
        status: status ? (status[0] as BidsFormValues["status"]) : undefined,
      };

      delete newValues.interview_status;

      return newValues;
    },
    mapParamsToFetchValues: (params) => {
      normalizeListParam(params, "ids");
      normalizeListParam(params, "skills");
      normalizeListParam(params, "top_skills");
    },
  },
  { className: styles.bidFiltersForm },
);

export const FilteringActions = ({
  isMatcher,
  isAiInterviewEnabled,
  job,
  isInterviewingTab,
  bidList,
}: FilteringActionsProps) => {
  const jobType = job.job_type;
  const jobId = job.id;
  const selectedBidIds = useAppSelector(getSelectedBidIds);
  const selectedBidsTotal = selectedBidIds.length;

  const shouldRender = selectedBidsTotal === 0;

  const options: FreelancerBidFilterOptionType = useMemo(
    () => getOptions(isMatcher, jobType),
    [isMatcher, jobType],
  );

  const formBidFilterOptions = useMemo(() => {
    const statusFilter: string[] = isAiInterviewEnabled
      ? [ENUMS.BidStatus.INTERVIEWING]
      : [ENUMS.JobInterviewStatus.ROUND_1, ENUMS.JobInterviewStatus.ROUND_2];

    return isInterviewingTab
      ? FORMS_BID_STATUS_FILTER_OPTIONS.filter(
          (option) =>
            ![
              ...statusFilter,
              ENUMS.BidStatus.NEW,
              ENUMS.BidStatus.VIEWED,
            ].includes(option.value),
        )
      : FORMS_BID_STATUS_FILTER_OPTIONS.filter(
          (option) => !statusFilter.includes(option.value),
        );
  }, [isAiInterviewEnabled, isInterviewingTab]);
  const orderingOptions = useMemo(() => {
    return [
      SORTING_OPTIONS.DATE,
      SORTING_OPTIONS.AI_SCORE,

      job.resume_smart_matching_enabled ? SORTING_OPTIONS.RESUME_SCORE : null,
    ].filter((option) => !!option);
  }, [job.resume_smart_matching_enabled]);

  if (!shouldRender) {
    return null;
  }

  return (
    <Box className={styles.bidFiltersActions}>
      <BidSelectionCheckbox bidList={bidList} />
      <BidListFiltersForm
        form="bid-filters"
        destroyOnUnmount={false}
        transformInitialValues={(values) => {
          if (
            !job.resume_smart_matching_enabled &&
            values.ordering === SORTING_OPTIONS.RESUME_SCORE.value
          ) {
            return { ...values, ordering: SORTING_OPTIONS.DATE.value };
          }

          return values;
        }}
      >
        <div className={styles.bidFiltersContent}>
          <Field
            id="talent_status"
            name="talent_status"
            component={SelectField}
            options={options}
            sx={{ py: "10px" }}
            displayEmpty={() => true}
            style={{
              borderRadius: "8px",
              height: "48px",
            }}
            noLabel
          />
          <Field
            name="status"
            id="bid-status"
            component={SelectField}
            options={formBidFilterOptions}
            sx={{ py: "10px" }}
            displayEmpty={() => true}
            style={{
              borderRadius: "8px",
              height: "48px",
            }}
            noLabel
          />

          <BidsTalentLocationFilter jobId={jobId} />

          <Fields
            names={["skills", "top_skills"]}
            component={BidSkillsFilter}
            job={job}
          />

          <Fields
            names={["reaction", "is_hidden"]}
            component={ReactionsFeedbackFilters}
          />

          <Field
            name="ordering"
            className={styles.bidFiltersSortBtn}
            component={SelectField}
            options={orderingOptions}
            noLabel
            IconComponent={CodeIcon}
            data-testid="ordering-select"
            sx={{
              "& .MuiSvgIcon-root": {
                right: "unset",
                left: "7px",
                transform: "rotate(90deg)",
                color: "var(--black)",
              },
              "& .MuiInputBase-input": {
                paddingLeft: "40px!important",
                paddingRight: "16px!important",
              },
            }}
            style={{
              borderRadius: "8px",
              height: "48px",
            }}
          />
        </div>
      </BidListFiltersForm>
    </Box>
  );
};
